/*
THESIS: Change the account email in one form.
OWN-WORLD: Inherit the workspace's neutral palette and shadcn controls.
STORY: Review the current email, edit it, save and see the confirmed result.
FIRST VIEWPORT: Account heading above a compact email field and Save button.
FORM: Extend the existing application layout with a single settings form.
*/
import { useCallback, useEffect, useRef, useState } from "react"
import { createFileRoute, Link } from "@tanstack/react-router"
import { ArrowLeft } from "lucide-react"
import { toast } from "sonner"
import { Alert, AlertDescription } from "@/components/ui/alert"
import { Button, buttonVariants } from "@/components/ui/button"
import {
  Field,
  FieldContent,
  FieldDescription,
  FieldGroup,
  FieldLabel,
  FieldLegend,
  FieldSet,
} from "@/components/ui/field"
import { Input } from "@/components/ui/input"
import { Spinner } from "@/components/ui/spinner"
import { Switch } from "@/components/ui/switch"
import { authClient } from "@/lib/auth-client"
import { videoClient } from "@/lib/video-client"
import { AccountPassword } from "@/components/app/account-password"

export const Route = createFileRoute("/app/account")({
  head: () => ({
    meta: [
      { title: "Account | intoshort" },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
  component: AccountPage,
})

function AccountPage() {
  const { data: session } = authClient.useSession()
  if (!session) return null

  return (
    <main className="app-main" aria-labelledby="account-title">
      <section className="flex w-full max-w-lg flex-col gap-8">
        <Link
          to="/app"
          className={buttonVariants({
            variant: "ghost",
            size: "sm",
            className: "app-video-back self-start",
          })}
        >
          <ArrowLeft data-icon="inline-start" aria-hidden="true" />
          Back to videos
        </Link>
        <h1 id="account-title" className="text-3xl font-semibold">
          Account
        </h1>
        <EmailForm
          key={session.user.id}
          userId={session.user.id}
          currentEmail={session.user.email}
        />
        <EmailNotificationsForm key={`notifications-${session.user.id}`} />
      </section>
    </main>
  )
}

type EmailNotifications = {
  shortsReadyEmailEnabled: boolean
  productUpdatesEmailEnabled: boolean
}

const notificationSaveDelayMs = 500

function EmailNotificationsForm() {
  const loadRequest = useRef(0)
  const saveRequest = useRef(0)
  const [persisted, setPersisted] = useState<EmailNotifications | null>(null)
  const [draft, setDraft] = useState<EmailNotifications | null>(null)
  const [loading, setLoading] = useState(true)
  const [pending, setPending] = useState(false)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [saveError, setSaveError] = useState<string | null>(null)

  const loadPreferences = useCallback(async () => {
    const request = ++loadRequest.current
    setLoading(true)
    setLoadError(null)
    try {
      const preferences = await videoClient.account.getEmailNotifications()
      if (request !== loadRequest.current) return
      setPersisted(preferences)
      setDraft(null)
      setSaveError(null)
    } catch {
      if (request !== loadRequest.current) return
      setLoadError(
        "We could not load your notification preferences. Check your connection and try again."
      )
    } finally {
      if (request === loadRequest.current) setLoading(false)
    }
  }, [])

  useEffect(() => {
    void loadPreferences()
    return () => {
      loadRequest.current += 1
    }
  }, [loadPreferences])

  const preferences = draft ?? persisted
  const changed =
    preferences !== null &&
    persisted !== null &&
    (preferences.shortsReadyEmailEnabled !==
      persisted.shortsReadyEmailEnabled ||
      preferences.productUpdatesEmailEnabled !==
        persisted.productUpdatesEmailEnabled)

  useEffect(() => {
    const request = ++saveRequest.current
    if (!preferences || !changed) return

    const timer = window.setTimeout(() => {
      setPending(true)
      setSaveError(null)
      void videoClient.account
        .updateEmailNotifications(preferences)
        .then((updated) => {
          if (request !== saveRequest.current) return
          setPersisted(updated)
          setDraft(null)
          toast.success("Notification preferences updated.")
        })
        .catch(() => {
          if (request !== saveRequest.current) return
          setSaveError(
            "We could not save your notification preferences. Check your connection and try again."
          )
        })
        .finally(() => {
          if (request === saveRequest.current) setPending(false)
        })
    }, notificationSaveDelayMs)

    return () => {
      window.clearTimeout(timer)
      if (request === saveRequest.current) saveRequest.current += 1
    }
  }, [changed, preferences])

  function updateDraft(key: keyof EmailNotifications, checked: boolean) {
    if (!preferences || pending) return
    setDraft({ ...preferences, [key]: checked })
    setSaveError(null)
  }

  return (
    <form className="pt-6" aria-busy={loading || pending}>
      <FieldSet>
        <FieldLegend>Email notifications</FieldLegend>
        {loading ? (
          <Field orientation="horizontal">
            <Spinner data-icon="inline-start" />
            <FieldDescription>
              Loading notification preferences...
            </FieldDescription>
          </Field>
        ) : loadError ? (
          <FieldGroup>
            <Alert variant="destructive">
              <AlertDescription>{loadError}</AlertDescription>
            </Alert>
            <Field orientation="horizontal">
              <Button type="button" variant="outline" onClick={loadPreferences}>
                Retry loading
              </Button>
            </Field>
          </FieldGroup>
        ) : preferences ? (
          <FieldGroup>
            <Field
              className="justify-between"
              orientation="horizontal"
              data-disabled={pending}
            >
              <FieldContent>
                <FieldLabel htmlFor="shorts-ready-email">
                  Shorts ready
                </FieldLabel>
                <FieldDescription>
                  Get notified when your Shorts are ready.
                </FieldDescription>
              </FieldContent>
              <Switch
                id="shorts-ready-email"
                name="shortsReadyEmailEnabled"
                checked={preferences.shortsReadyEmailEnabled}
                disabled={pending}
                onCheckedChange={(checked) =>
                  updateDraft("shortsReadyEmailEnabled", checked)
                }
              />
            </Field>
            <Field
              className="justify-between"
              orientation="horizontal"
              data-disabled={pending}
            >
              <FieldContent>
                <FieldLabel htmlFor="product-updates-email">
                  Product updates
                </FieldLabel>
                <FieldDescription>
                  Get notified when we release new features.
                </FieldDescription>
              </FieldContent>
              <Switch
                id="product-updates-email"
                name="productUpdatesEmailEnabled"
                checked={preferences.productUpdatesEmailEnabled}
                disabled={pending}
                onCheckedChange={(checked) =>
                  updateDraft("productUpdatesEmailEnabled", checked)
                }
              />
            </Field>
            {saveError && (
              <Alert variant="destructive">
                <AlertDescription>{saveError}</AlertDescription>
              </Alert>
            )}
          </FieldGroup>
        ) : null}
      </FieldSet>
    </form>
  )
}

function EmailForm({
  userId,
  currentEmail,
}: {
  userId: string
  currentEmail: string
}) {
  const [draft, setDraft] = useState<string | null>(null)
  const [confirmedEmail, setConfirmedEmail] = useState<string | null>(null)
  const [pending, setPending] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [saved, setSaved] = useState(false)
  const persistedEmail = confirmedEmail ?? currentEmail
  const email = draft ?? persistedEmail
  const changed = email.trim().toLowerCase() !== persistedEmail.toLowerCase()

  async function saveEmail(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault()
    if (pending || !changed) return
    setPending(true)
    setError(null)
    setSaved(false)
    const newEmail = email.trim().toLowerCase()
    try {
      const result = await authClient.changeEmail({ newEmail })
      if (result.error) {
        setError(
          result.error.status === 401 || result.error.status === 403
            ? "Sign in again before changing your email."
            : result.error.code === "INVALID_EMAIL"
              ? "This email address is invalid."
              : result.error.status === 429
                ? "Too many requests. Wait a minute before trying again."
                : "We could not send your email confirmation. Please try again."
        )
        return
      }
      // A request is not a saved change. Confirm persisted state first.
      const refreshed = await authClient.getSession({
        query: { disableCookieCache: true },
      })
      if (refreshed.error || !refreshed.data) {
        setError(
          "We could not confirm the change. Reload this page to check your email."
        )
        return
      }
      if (refreshed.data.user.email.toLowerCase() !== newEmail) {
        // Better Auth also returns success for an address already in use.
        toast.success(
          "If this address is available, a confirmation link has been sent."
        )
        return
      }
      setConfirmedEmail(refreshed.data.user.email)
      setDraft(null)
      setSaved(true)
    } catch {
      setError(
        "We could not confirm the change. Check your connection and reload this page before trying again."
      )
    } finally {
      setPending(false)
    }
  }

  return (
    <form onSubmit={saveEmail} aria-busy={pending}>
      <FieldGroup>
        <Field
          data-disabled={pending}
          data-invalid={error === "This email address is invalid."}
        >
          <FieldLabel htmlFor="account-email">Email</FieldLabel>
          <Input
            id="account-email"
            name="email"
            type="email"
            autoComplete="email"
            required
            value={email}
            disabled={pending}
            aria-invalid={error === "This email address is invalid."}
            onChange={(event) => {
              setDraft(event.target.value)
              setError(null)
              setSaved(false)
            }}
          />
        </Field>
        {error && (
          <Alert variant="destructive">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}
        {saved && (
          <Alert role="status">
            <AlertDescription>Email saved.</AlertDescription>
          </Alert>
        )}
        <AccountPassword userId={userId} email={persistedEmail}>
          <Button
            type="submit"
            className="w-fit"
            disabled={pending || !changed}
          >
            {pending && <Spinner data-icon="inline-start" />}
            {pending ? "Saving..." : "Save"}
          </Button>
        </AccountPassword>
      </FieldGroup>
    </form>
  )
}
